import { useState, useEffect } from 'react';
import { StudyData, SeriesData } from '../cornerstone/init';
import { useAuthStore } from '../stores/authStore';
import { getServerUrl, apiCall } from '../api/client';
import { KeyImage, keyImageThumbUrl } from '../utils/keyImages';
import { getProvider } from '../data/providers';
import { IconStar } from './ToolIcons';
import styles from './SeriesPanel.module.css';

interface PriorStudy {
  studyId: number;
  studyDate: string;
  studyDescription: string;
  modality: string;
  numImages: number;
}

export type ReportStatus = 'none' | 'draft' | 'signed';

interface Props {
  /** All loaded studies — rendered as collapsible tree nodes; only the
   *  active one is expanded at any time. */
  studies: StudyData[];
  activeStudyId: number | null;
  onActiveStudyChange?: (studyId: number) => void;
  /** Remove a study from the loaded-studies set. Only rendered when ≥2
   *  studies are open — closing the last one would leave the viewer empty. */
  onCloseStudy?: (studyId: number) => void;
  loadedIds?: Set<string>;
  /** Called when user clicks a series thumbnail to load it */
  onSeriesClick?: (seriesId: number) => void;
  /** Currently loaded series ID in active viewport */
  activeSeriesId?: number | null;
  /** Current report state for the ACTIVE study — controls Report button caption */
  reportStatus?: ReportStatus;
  onOpenReport?: () => void;
  /** Flagged key images for the active study — compact nav strip at the top. */
  keyImages?: KeyImage[];
  onJumpKeyImage?: (k: KeyImage) => void;
  onDeleteKeyImage?: (k: KeyImage) => void;
  /** Bulk-add every image in a series to the composer basket. Rendered as
   *  a small "+" button on each series row. */
  onAddSeriesToComposerBasket?: (seriesId: number) => void;
}

export default function SeriesPanel({
  studies, activeStudyId, onActiveStudyChange, onCloseStudy,
  loadedIds, onSeriesClick, activeSeriesId, reportStatus = 'none', onOpenReport,
  keyImages = [], onJumpKeyImage, onDeleteKeyImage,
  onAddSeriesToComposerBasket,
}: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const [priors, setPriors] = useState<PriorStudy[]>([]);
  const [showPriors, setShowPriors] = useState(false);
  const acctoken = useAuthStore((s) => s.acctoken);

  const activeStudy = studies.find((s) => s.studyId === activeStudyId) ?? studies[0] ?? null;

  useEffect(() => {
    if (!activeStudy?.studyId) return;
    if (!getProvider().supports('priors')) { setPriors([]); return; }
    apiCall<PriorStudy>('study.priors', { studyId: activeStudy.studyId }).then((res) => {
      if (res.s === 'ok') setPriors(res.data || []);
    });
  }, [activeStudy?.studyId]);

  if (studies.length === 0 || !activeStudy) return null;

  function onDragStart(e: React.DragEvent, series: SeriesData) {
    e.dataTransfer.setData('seriesId', String(series.seriesId));
    e.dataTransfer.effectAllowed = 'copy';
  }

  function getSeriesProgress(series: SeriesData): number {
    if (!loadedIds || !acctoken || loadedIds.size === 0) return 0;
    let loaded = 0;
    for (let i = 0; i < series.numImages; i++) {
      const imageId = `wadouri:${getServerUrl()}/atlrestapi/v2/dicom?seriesId=${series.seriesId}&imageIndex=${i}&acctoken=${acctoken}`;
      if (loadedIds.has(imageId)) loaded++;
    }
    return series.numImages > 0 ? Math.round((loaded / series.numImages) * 100) : 0;
  }

  return (
    <div className={`${styles.panel} ${collapsed ? styles.collapsed : ''}`}>
      <div className={styles.header}>
        <button
          className={styles.toggleBtn}
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? '▶' : '◀'}
        </button>
        {!collapsed && <span className={styles.title}>Series</span>}
      </div>
      {!collapsed && (
        <div className={styles.list}>
          {/* Multi-study tree: each loaded study is a collapsible node.
              Only the active study is expanded; clicking another study's
              header makes it active and expands its tree. */}
          {studies.map((study) => {
            const isActive = study.studyId === activeStudy.studyId;
            return (
              <div key={study.studyId} className={styles.studyNode}>
                <div
                  className={`${styles.studyHeader} ${isActive ? styles.studyHeaderActive : ''}`}
                  onClick={() => onActiveStudyChange?.(study.studyId)}
                  title="Click to expand this study"
                >
                  <span className={styles.studyChevron}>{isActive ? '▼' : '▶'}</span>
                  <div className={styles.studyHeaderText}>
                    <div className={styles.studyHeaderName}>{study.patientName}</div>
                    <div className={styles.studyHeaderMeta}>
                      {study.studyDate}
                      {study.modality ? ` · ${study.modality}` : ''}
                      {study.studyDescription ? ` · ${study.studyDescription}` : ''}
                    </div>
                  </div>
                  {onCloseStudy && studies.length > 1 && (
                    <button
                      className={styles.studyCloseBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCloseStudy(study.studyId);
                      }}
                      title="Remove this study from the viewer"
                    >
                      &times;
                    </button>
                  )}
                </div>

                {isActive && (
                  <div className={styles.studyBody}>
                    <div className={styles.patientInfo}>
                      <div className={styles.patientDetail}>ID: {study.patientId}</div>
                      {study.patientSex && <div className={styles.patientDetail}>Sex: {study.patientSex}</div>}
                      {study.patientAge && <div className={styles.patientDetail}>Age: {study.patientAge}</div>}
                    </div>

                    {onOpenReport && (
                      <button
                        className={`${styles.reportBtn} ${styles['reportBtn_' + reportStatus]}`}
                        onClick={onOpenReport}
                        title={
                          reportStatus === 'signed' ? 'Report signed — click to view'
                          : reportStatus === 'draft' ? 'Draft in progress — click to modify'
                          : 'No report yet — click to create'
                        }
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14,2 14,8 20,8" />
                          <line x1="8" y1="13" x2="16" y2="13" />
                          <line x1="8" y1="17" x2="14" y2="17" />
                        </svg>
                        <span>
                          {reportStatus === 'signed' ? 'View Report'
                            : reportStatus === 'draft' ? 'Modify Report'
                            : 'Create Report'}
                        </span>
                      </button>
                    )}

                    {keyImages.length > 0 && (
                      <div className={styles.keyImagesSection}>
                        <div className={styles.keyImagesHeader}>
                          <IconStar size={12} />
                          <span>Key Images ({keyImages.length})</span>
                        </div>
                        <div className={styles.keyImagesGrid}>
                          {keyImages.map((k) => (
                            <div
                              key={k.keyImageId}
                              className={styles.keyImageTile}
                              onClick={() => onJumpKeyImage?.(k)}
                              title={k.note || `Series ${k.seriesId}, slice ${k.imageIndex + 1}`}
                            >
                              <img
                                className={styles.keyImageThumb}
                                src={keyImageThumbUrl(getServerUrl(), k.seriesId, k.imageIndex, acctoken)}
                                alt=""
                                loading="lazy"
                              />
                              <span className={styles.keyImageBadge}>#{k.imageIndex + 1}</span>
                              {onDeleteKeyImage && (
                                <button
                                  className={styles.keyImageRemove}
                                  onClick={(e) => { e.stopPropagation(); onDeleteKeyImage(k); }}
                                  title="Remove"
                                >
                                  &times;
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {study.series.map((series) => {
                      const pct = getSeriesProgress(series);
                      const isActiveSeries = activeSeriesId === series.seriesId;

                      return (
                        <div
                          key={series.seriesId}
                          className={`${styles.seriesItem} ${isActiveSeries ? styles.selected : ''}`}
                          onClick={() => {
                            // Clicking a series in a non-active study should
                            // also make it active so the toolbar/overlay
                            // reflect the right context.
                            if (!isActive) onActiveStudyChange?.(study.studyId);
                            onSeriesClick?.(series.seriesId);
                          }}
                          draggable
                          onDragStart={(e) => onDragStart(e, series)}
                        >
                          <div className={styles.seriesThumbnail}>
                            <img
                              className={styles.thumbImg}
                              src={getProvider().thumbnailUrl(study, series, acctoken || undefined)}
                              alt={series.modality}
                              loading="lazy"
                              onError={(e) => {
                                const img = e.currentTarget;
                                img.style.display = 'none';
                                const fallback = img.nextElementSibling as HTMLElement | null;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                            <div className={styles.placeholderThumb} style={{ display: 'none' }}>
                              {series.modality}
                            </div>
                          </div>
                          <div className={styles.seriesInfo}>
                            <div className={styles.seriesDesc}>
                              {series.seriesDescription || `Series ${series.seriesNumber}`}
                            </div>
                            <div className={styles.seriesMeta}>
                              {series.modality} &middot; {series.numImages} img
                            </div>
                          </div>
                          {onAddSeriesToComposerBasket && (
                            <button
                              type="button"
                              className={styles.seriesAddBasketBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddSeriesToComposerBasket(series.seriesId);
                              }}
                              title={`Add all ${series.numImages} images to Composer basket`}
                            >+</button>
                          )}
                          {pct > 0 && pct < 100 && (
                            <div className={styles.seriesProgress}>
                              <div
                                className={styles.seriesProgressFill}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          )}
                          {pct === 100 && (
                            <div className={styles.seriesProgressDone} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Prior Studies — only listed for the active study */}
          {priors.length > 0 && (
            <>
              <div
                className={styles.priorsHeader}
                onClick={() => setShowPriors(!showPriors)}
              >
                <span>Prior Studies ({priors.length})</span>
                <span>{showPriors ? '▼' : '▶'}</span>
              </div>
              {showPriors && priors.map((prior) => (
                <a
                  key={prior.studyId}
                  className={styles.priorItem}
                  href={`#/viewer/${prior.studyId}`}
                  target="_blank"
                  rel="noopener"
                >
                  <div className={styles.priorDate}>{prior.studyDate}</div>
                  <div className={styles.priorDesc}>
                    {prior.modality} — {prior.studyDescription}
                  </div>
                  <div className={styles.priorMeta}>{prior.numImages} img</div>
                </a>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
